/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import { Node, mergeAttributes } from "@tiptap/core";
import { createTable } from "@tiptap/extension-table";
import { Node as ProseMirrorNode } from "prosemirror-model";

export interface TableWrapperOptions {
  type: "tableWrapper"
  attrs: {
    class: string | null
  }
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    tableWrapper: {
      insertTableWrapper: () => ReturnType;
      toggleWrapperClass: (className: string) => ReturnType;
    };
  }
}

export const TableWrapper = Node.create<null, TableWrapperOptions>({
  name: "tableWrapper",

  content: "table",

  group: "block",

  parseHTML() {
    return [{ tag: 'div[data-type="table-wrapper"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, { "data-type": "table-wrapper" }),
      0,
    ];
  },

  addAttributes() {
    return {
      class: {
        default: null
      }
    };
  },

  addCommands() {
    return {
      insertTableWrapper: () => {
        return ({ tr, dispatch, editor }) => {
          const tableNode = createTable(editor.schema, 2, 2, false)

          const wrapperNode = this.type.create(null, tableNode);

          if (dispatch) {
            tr.replaceSelectionWith(wrapperNode).scrollIntoView();
            dispatch(tr);
          }

          return true
        }
      },
      toggleWrapperClass: (className: string) => {
        return ({ tr, dispatch, state })=> {
          const { selection } = state;
          const $pos = selection.$anchor;

          let tableWrapperNode: ProseMirrorNode | null = null;
          let tableWrapperPos: number = -1;

          for (let i = $pos.depth; i > 0; i--) {
            const node = $pos.node(i);

            if (node.type.name === 'tableWrapper') {
              tableWrapperNode = node;
              tableWrapperPos = $pos.before(i);
              break;
            }
          }

          if (!tableWrapperNode || tableWrapperPos === -1 || !className) {
            return false;
          }

          const currentAttrs = tableWrapperNode.attrs;

          const currentClassString: string = (currentAttrs.class || '') as string;
          const classList = new Set(currentClassString.split(' ').filter(c => c));

          if (classList.has(className)) {
            classList.delete(className);
          } else {
            classList.add(className);
          }

          const newClassString = Array.from(classList).join(' ');

          if (dispatch) {
            tr.setNodeMarkup(
              tableWrapperPos,
              undefined,
              {
                ...currentAttrs,
                class: newClassString.trim(),
              }
            )

            dispatch(tr);
          }

          return true
        }
      }
    };
  },
});

export default TableWrapper;
